import React from 'react';
import { Link } from '@/components/link';

import { Currency, currencySymbols } from '@/helpers/currency';
import { formatDate, isToday } from '@/helpers/date';
import { formatNumber } from '@/helpers/formatNumber';
import { useRouter } from 'next/router';

type Props = {
  ticker: string;
  market: string;
  currentPrice: number;
  lastValueAt: string;
  lastChange: number;
  lastChangePercent: number;
  shares: number;
  stock: string;
  stockCurrency: Currency;
  buyPrice: number;
  portfolio: { id: string; name: string; currency: Currency };
  portfolioWeight: number;
  returnsInPortfolioCurrency: number;
  yieldInPortfolioCurrency: number;
  returnsInStockCurrency: number;
  yieldInStockCurrency: number;
  onCardClick: () => void;
};

export const TransactionCard: React.FC<Props> = ({
  ticker,
  currentPrice,
  lastValueAt,
  lastChange,
  lastChangePercent,
  shares,
  stock,
  stockCurrency,
  buyPrice,
  portfolio,
  portfolioWeight,
  returnsInPortfolioCurrency,
  yieldInPortfolioCurrency,
  returnsInStockCurrency,
  yieldInStockCurrency,
  onCardClick,
}) => {
  const router = useRouter();
  const isChangePositive = lastChange >= 0;
  const [date, time] = lastValueAt.split(' ');

  return (
    <div
      className="cursor-pointer items-center rounded bg-white p-6 text-gray-600 shadow-xl"
      onClick={onCardClick}
      data-testid="transactionCard"
    >
      <div className="flex flex-col">
        <h3 className="truncate text-lg font-semibold capitalize leading-tight text-gray-800">{stock}</h3>
        <h6 className="text-sm leading-tight">
          {ticker} - {isToday(date) ? time : formatDate(date)}
        </h6>
        <div className="my-4 flex w-full items-end">
          <span className="block text-3xl leading-none text-gray-800">{currentPrice}</span>
          <span className={`ml-4 block text-sm leading-5 text-${isChangePositive ? 'green' : 'red'}-500`}>
            {isChangePositive ? '▲ ' : '▼ '}
            {Math.abs(lastChange)} ({formatNumber(lastChangePercent)} %)
          </span>
        </div>
        <div className="flex w-full text-xs">
          <div className="flex w-5/12" data-testid="cardShares">
            <div className="flex-1 pr-3 text-left font-semibold">Posición</div>
            <div className="text-right">{shares}</div>
          </div>
          <div className="flex w-7/12" data-testid="cardPortfolio">
            <div className="flex-1 px-3 text-left font-semibold">Cartera</div>
            <div className="text-right hover:underline">
              <Link
                href={`/portfolios/${portfolio.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  event.nativeEvent.stopImmediatePropagation();
                  router.push(`/portfolios/${portfolio.id}`);
                }}
              >
                {portfolio.name}
              </Link>
            </div>
          </div>
        </div>
        <div className="flex w-full text-xs">
          <div className="flex w-5/12" data-testid="cardBuyPrice">
            <div className="flex-1 pr-3 text-left font-semibold">Compra</div>
            <div className="text-right">{buyPrice}</div>
          </div>
          <div className="flex w-7/12" data-testid="cardPortfolioWeight">
            <div className="flex-1 px-3 text-left font-semibold">Peso cartera</div>
            <div className="text-right">{formatNumber(portfolioWeight)} %</div>
          </div>
        </div>

        {stockCurrency !== portfolio.currency && (
          <div className="flex w-full text-xs">
            <div className="flex w-5/12" data-testid="cardCurrencyReturns">
              <div className="flex-1 pr-3 text-left font-semibold">B/P ({currencySymbols[stockCurrency]})</div>
              <div className="text-right">{formatNumber(returnsInStockCurrency)}</div>
            </div>
            <div className="flex w-7/12" data-testid="cardCurrencyYield">
              <div className="flex-1 px-3 text-left font-semibold">Rent. Pot. ({currencySymbols[stockCurrency]})</div>
              <div className="text-right">{formatNumber(yieldInStockCurrency)} %</div>
            </div>
          </div>
        )}

        <div className="flex w-full text-xs">
          <div className="flex w-5/12" data-testid="cardEuroReturns">
            <div className="flex-1 pr-3 text-left font-semibold">B/P ({currencySymbols[portfolio.currency]})</div>
            <div
              className={`text-right text-${
                Number(formatNumber(returnsInPortfolioCurrency)) >= 0 ? 'green' : 'red'
              }-500`}
            >
              {formatNumber(returnsInPortfolioCurrency)}
            </div>
          </div>
          <div className="flex w-7/12" data-testid="cardEuroYield">
            <div className="flex-1 px-3 text-left font-semibold">
              Rent. Pot. ({currencySymbols[portfolio.currency]})
            </div>
            <div className="text-right">{formatNumber(yieldInPortfolioCurrency)} %</div>
          </div>
        </div>
      </div>
    </div>
  );
};
